import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { BsCloudUpload } from "react-icons/bs";
import { BASE_URL } from "./Constants";
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { PaperClipIcon } from "@heroicons/react/24/outline";
import api from "./components/login/interceptors";
import CandidateStatusToggle from "./components/modals/candidate/CandidateStatusToggle";
import { Box, Button, Chip, FormControl, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Tooltip, Typography, useTheme } from "@mui/material";
import cn from "./utils/cn";
import SkillChipsPopover from "./components/table/SkillChipPopover";
import LimitReachedModal from "./components/modals/candidate/LimitReachedModal";
import { resumeLimitMessage } from "./utils/organisationApi";
import usePlanGate from "./hooks/usePlanGate";
import { CiSearch } from "react-icons/ci";
import { PaginationButtons, RowsPerPageSelector } from "./utils/paginationUtils";
import useScreenWidth from "./hooks/useScreenWidth";
import useFocusTrap from './hooks/useFocusTrap';
import TopStarIcon from "./icons/TopStarIcon";
import RisingStar from "./icons/RisingStar";
import { formatFileSize, truncateFilename, MAX_FILE_LIMIT, MAX_FILE_SIZE_BYTES, allowedFileTypes } from "./utils/fileUploadUtils";
import { GlobalLoader } from "./components/loaders/GlobalLoader";
import { ErrorToast } from "./components/modals/ErrorToast";
import { createPortal } from "react-dom";
import TabbedUploadModal from "./components/modals/TabbedUploadModal";
import { validateEmail } from "./components/auth/validationHelpers";
import ResumeViewer from "./components/modals/ResumeViewerModal";

const bulkFileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;

const emailCodeMessage = (code) => {
  if (code === "EMAIL_REQUIRED") return "Email is required";
  if (code === "EMAIL_INVALID") return "Enter a valid email";
  return "";
};

const bulkFieldMessage = (field, code) => {
  const emailMessage = emailCodeMessage(code);
  if (emailMessage) return emailMessage;
  if (field === "name" || field === "names") return "Name is required";
  return code ? String(code).replaceAll("_", " ").toLowerCase() : "Invalid value";
};

const bulkUploadErrorMessage = (error, uploadedFiles) => {
  const data = error?.response?.data || {};
  const errors = Array.isArray(data.errors) ? data.errors : [];
  if (errors.length) {
    return errors
      .map((item) => {
        const fileName = uploadedFiles[item.index]?.name || `File ${Number(item.index) + 1}`;
        return `${fileName}: ${bulkFieldMessage(item.field, item.code)}`;
      })
      .join(" ");
  }
  return (
    emailCodeMessage(data.code || data.error) ||
    data.message ||
    (typeof data.error === "string" ? data.error : "") ||
    "Upload properly formatted resumes."
  );
};

const CandidatesPage = ({
  jobId,
  isArchive,
  jobExperience,
}) => {
  const [jobTitle, setJobTitle] = useState(null);
  const [jobDescription, setJobDescription] = useState(null);
  const [checkedSkills, setCheckedSkills] = useState([]);
  const [parsedSkills, setParsedSkills] = useState([]);
  const [preferredSkills, setPreferredSkills] = useState([]);
  const [mandatorySkills, setMandatorySkills] = useState([]);
  const [aliasSkills, setAliasSkills] = useState({})
  const [jdMinExp, setJdMinExp] = useState(null);
  const [jdProvider, setJdProvider] = useState("")
  const [jdStatus, setJdStatus] = useState(null);
  const [candidate, setCandidate] = useState(null); // Used for ResumeViewer component
  const [candidates, setCandidates] = useState([]);
  const [isCandidateLoading, setIsCandidateLoading] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [tblErrorMessage, setTblErrorMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isAddNewCandidateModalOpen, setIsAddNewCandidateModalOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [bulkDetails, setBulkDetails] = useState({});

  useEffect(() => {
    if (files.length === 0) {
      setBulkDetails((prev) => (Object.keys(prev).length ? {} : prev));
    }
  }, [files]);
  const [isEvaluateButtonEnabled, setIsEvaluateButtonEnabled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isModalConfirmed, setIsModalConfirmed] = useState(false);
  const [tooltipIndex, setTooltipIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [atsName, setAtsName] = useState();
  const [atsId, setAtsId] = useState();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [candidateModalError, setCandidateModalError] = useState("")
  const [job, setJob] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSyncComplete, setIsSyncComplete] = useState(false);
  const [isAddCandidateModalOpen, setIsAddCandidateModalOpen] = useState(false);
  const [candidateName, setCandidateName] = useState("");
  const [candidateEmail, setCandidateEmail] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploadingCandidate, setIsUploadingCandidate] = useState(false);
  const [addCandidateError, setAddCandidateError] = useState("");
  const addCandidateFileInputRef = useRef(null);
  const [isTabbedUploadModalOpen, setIsTabbedUploadModalOpen] = useState(false);
  const [resumeUrl, setResumeUrl] = useState(null);
  const resumeModalRef = useRef(null);

  const [sortField, setSortField] = useState("status");
  const [sortOrder, setSortOrder] = useState("asc");
  const [searchQuery, setSearchQuery] = useState("");
  const { screenWidth } = useScreenWidth();

  const [isLimitReachedModalOpen, setIsLimitReachedModalOpen] = useState(false);
  const [resumeLimitNotice, setResumeLimitNotice] = useState("");
  const [rankingOpen, setRankingOpen] = useState(false);
  const theme = useTheme();
  // Resume uploads are limited by the plan's parsing allowance
  const uploadGate = usePlanGate("resume_parses");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const firstHeaderCellRef = useRef(null);

  const addCandidateModalRef = useFocusTrap(isAddNewCandidateModalOpen, () => setIsAddNewCandidateModalOpen(false));
  const addCandidateListModalRef = useFocusTrap(isAddCandidateModalOpen, () => setIsAddCandidateModalOpen(false));
  const reEvaluateModalRef = useFocusTrap(isModalOpen, () => setIsModalOpen(false));

  const [rankingFilter, setRankingFilter] = useState("all");
  const navigate = useNavigate();

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getColumnWidths = () => {
    if (jobId) {
      return {
        checkbox: '48px',
        name: '220px',
        skills: '280px',
        expScore: '200px',
        overallScore: '120px',
        status: '140px',
      };
    } else {
      return {
        name: '220px',
        email: '240px',
        parsedSkills: '300px',
        jdResumes: '120px',
        lastUpdated: '180px',
      };
    }
  };

  const widths = getColumnWidths();
  const calculateRankings = useMemo(() => {
    if (!candidates.length) return { topStars: [], risingStars: [] };

    const topStars = [...candidates]
      .sort((a, b) => b.final_score - a.final_score)
      .slice(0, 10)
      .map(c => c.lead_id);

    const risingStars = [...candidates]
      .filter(c => {
        const expScore = Math.min((c.resume_exp / jobExperience) * 10, 10);
        const skillScore = Math.min(((c.matched_skills?.length || 0) / (mandatorySkills.length + preferredSkills.length)) * 10, 10);
        return expScore < 7 && skillScore > 7;
      })
      .sort((a, b) => {
        const aSkillScore = Math.min(((a.matched_skills?.length || 0) / (mandatorySkills.length + preferredSkills.length)) * 10, 10);
        const bSkillScore = Math.min(((b.matched_skills?.length || 0) / (mandatorySkills.length + preferredSkills.length)) * 10, 10);
        return bSkillScore - aSkillScore;
      })
      .slice(0, 10)
      .map(c => c.lead_id);

    return { topStars, risingStars };
  }, [candidates, jobExperience, mandatorySkills.length, preferredSkills.length]);

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        const response = await api.get(`${BASE_URL}/api/job-desc/${jobId}`);
        if (response.data?.length > 0) {
          const jobData = response.data[0];
          setJob(jobData || "");
          setJobTitle(jobData.title || jobData.ats_title || "Unknown Job");
          setAtsName(jobData.ats_title || "")
          setAtsId(jobData.ats_ref_id || "")
          setJdProvider(jobData.provider || "");
          setJobDescription(jobData.job_description || "");
          setCheckedSkills(jobData.user_selected_skills || []);
          setParsedSkills(jobData.parsed_skills || []);
          setPreferredSkills(jobData.preferred_skills || []);
          setMandatorySkills(jobData.mandatory_skills || []);
          setAliasSkills(jobData.skills_alias || {});
          setJdMinExp(jobData.jd_min_exp || 0);
          setJdStatus(jobData.status);
        }
      } catch (error) {
        console.error("Error fetching job details:", error);
      }
    };

    if (jobId) fetchJobDetails();
    fetchCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    setJdStatus(isArchive ? "archived" : "active")
  }, [isArchive])

  const fetchCandidates = async () => {
    setIsCandidateLoading(true);
    try {
      let candidateUrl;
      let jobdescId = null;
      
      if (jobId) {
        // Fetch job description to get jobdesc_id (required to construct jd/candidates URL)
        try {
          const jobResponse = await api.get(`${BASE_URL}/api/job-desc/${jobId}`);
          const jobData = Array.isArray(jobResponse.data) ? jobResponse.data[0] : jobResponse.data;
          jobdescId = jobData?.jobdesc_id;
          
          if (jobdescId) {
            // Use the new endpoint with jobdesc_id - fetch immediately after getting jobdescId
            candidateUrl = `${BASE_URL}/api/jd/${jobdescId}/candidates`;
          } else {
            console.error("No jobdesc_id found in job description");
            candidateUrl = `${BASE_URL}/api/candidates/`;
          }
        } catch (error) {
          console.error("Error fetching job description:", error);
          candidateUrl = `${BASE_URL}/api/candidates/`;
        }
      } else {
        candidateUrl = `${BASE_URL}/api/candidates/`;
      }
      
      // Immediately fetch /api/jd API after getting jobdescId (don't wait for other operations)
      const candidateResponse = await api.get(candidateUrl);
      let relevantCandidates = candidateResponse.data || [];
      
      // Handle both array and object responses
      if (!Array.isArray(relevantCandidates)) {
        // If it's an object, try to extract an array from it
        if (relevantCandidates && typeof relevantCandidates === 'object') {
          // Check common response structures
          relevantCandidates = relevantCandidates.candidates || relevantCandidates.data || relevantCandidates.results || [];
        } else {
          relevantCandidates = [];
        }
      }

      // Fetch job descriptions in parallel (don't block candidate display)
      const jobResponsePromise = api.get(`${BASE_URL}/api/job-descriptions/`).catch((error) => {
        console.error("Error fetching job descriptions:", error);
        return { data: [] };
      });


      // Start processing candidates immediately while job descriptions load
      const jobResponse = await jobResponsePromise;
      const jobDescriptions = jobResponse.data || [];

      const jobTitleMap = {};
      jobDescriptions.forEach(job => {
        jobTitleMap[job.ats_ref_id || job.jd_id] = job.title || job.ats_title;
      });

      relevantCandidates = relevantCandidates.map(candidate => {
        // Map candidate_id to lead_id for compatibility with existing code
        const mappedCandidate = {
          ...candidate,
          // Use candidate_id as lead_id if lead_id doesn't exist
          lead_id: candidate.lead_id || candidate.candidate_id,
          // Ensure candidate_id exists
          candidate_id: candidate.candidate_id || candidate.lead_id,
          // Map name to first_name/last_name if needed
          first_name: candidate.first_name || (candidate.name ? candidate.name.split(' ')[0] : ''),
          last_name: candidate.last_name || (candidate.name ? candidate.name.split(' ').slice(1).join(' ') : ''),
          // Ensure numeric fields have defaults
          resume_exp: candidate.resume_exp ? Math.round(parseFloat(candidate.resume_exp)) : 0,
          jd_mapping_count: candidate.jd_mapping_count || 0,
          // Map job title
          job_title: jobTitleMap[candidate.job_id] || candidate.job_title || 'Unknown Position',
          email: candidate.email || "",
          schedulable: typeof candidate.schedulable === "boolean" ? candidate.schedulable : Boolean(candidate.email),
          // Ensure array fields have defaults
          resume_skills: candidate.resume_skills || candidate.parsed_skills || [],
          matched_skills: candidate.matched_skills || [],
          parsed_skills: candidate.parsed_skills || [],
          email: candidate.email || "",
        };
        return mappedCandidate;
      });

      setCandidates(relevantCandidates);
      setFilteredCandidates(relevantCandidates);
      return relevantCandidates;
    } catch (error) {
      console.error("Error fetching candidates:", error);
      // Set empty arrays on error to prevent undefined state
      setCandidates([]);
      setFilteredCandidates([]);
      return [];
    } finally {
      setIsCandidateLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const a = filteredCandidates.map((c) => c.lead_id);
      setSelectedCandidates(a);
    } else {
      setSelectedCandidates([]);
    }
  };

  const handleSelectCandidate = (lead_id) => {
    setSelectedCandidates((prev) =>
      prev.includes(lead_id)
        ? prev.filter((doc) => doc !== lead_id)
        : [...prev, lead_id]
    );
  };

  const handleBulkUpdate = async () => {
    if (selectedCandidates.length === 0) {
      alert("No candidates selected for deletion.");
      return;
    }

    setIsUpdating(true);
    setTblErrorMessage(null);

    try {
      const remainingCandidates = candidates.filter(
        (candidate) => !selectedCandidates.includes(candidate.lead_id)
      );

      await api.post(`${BASE_URL}/api/candidates/${jobId}`,
        { lead_ids: selectedCandidates, status: 'archived' }
      );
      setCandidates(remainingCandidates);
      setFilteredCandidates(remainingCandidates);
      setSelectedCandidates([]);
    } catch (error) {
      console.error("Error deleting candidates:", error);
      setTblErrorMessage("Something went wrong with the server. Please try again later.");
      setTimeout(() => {
        setTblErrorMessage("");
      }, 5000);
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePopUpModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalConfirm = () => {
    setIsModalOpen(false);
    setIsModalConfirmed(true);
  };

  // Link a single candidate to JD before navigation
  // Note: This function is currently unused but kept for potential future use
  // eslint-disable-next-line no-unused-vars
  const linkCandidateToJD = async (candidateId) => {
    if (!candidateId || !jobId) return;
    
    try {
      const token = localStorage.getItem("jwtToken");
      if (!token) return;

      // Fetch candidate data to get candidate_id
      const candidateResponse = await api.get(`${BASE_URL}/api/candidates/${candidateId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const candidateData = candidateResponse.data;
      const actualCandidateId = candidateData?.candidate_id;

      // Fetch job description to get jobdesc_id
      const jobResponse = await api.get(`${BASE_URL}/api/job-desc/${jobId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const jobData = Array.isArray(jobResponse.data) ? jobResponse.data[0] : jobResponse.data;
      const jobdescId = jobData?.jobdesc_id;

      if (!actualCandidateId || !jobdescId) {
        console.error("Missing candidate_id or jobdesc_id");
        return;
      }

      // Use candidate_id and jobdesc_id from the API responses
      await api.post(
        `${BASE_URL}/api/candidate-jd`,
        {
          candidate_id: actualCandidateId,
          jd_id: jobdescId, // jobdesc_id is the same as jd_id
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
    } catch (error) {
      console.error(`Error linking candidate ${candidateId} to JD:`, error);
      // Don't block navigation on error
    }
  };

  // Trigger standardized resume and candidate-JD linking for a single candidate after upload
  const triggerSinglePostUploadAPIs = async (candidateId) => {
    try {
      const token = localStorage.getItem("jwtToken");
      if (!token) {
        console.error("No JWT token found");
        return;
      }
      if (!jobId) {
        console.error("No jobId found");
        return;
      }

      // Fetch job description to get jobdesc_id
      const jobResponse = await api.get(`${BASE_URL}/api/job-desc/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const jobData = Array.isArray(jobResponse.data) ? jobResponse.data[0] : jobResponse.data;
      const jobdescId = jobData?.jobdesc_id;

      if (!jobdescId) {
        console.error("No jobdesc_id found in job description");
        return;
      }

      // Fetch template ID
      const templateResponse = await api.get(`${BASE_URL}/api/templates/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const templateId = templateResponse.data?._id || templateResponse.data?.template_id;
      if (!templateId) {
        console.error("No template ID found for standardized resume creation");
        return;
      }

      const promises = [];

      // Trigger standardized resume creation
      promises.push(
        api.post(
          `${BASE_URL}/api/resume/standardized`,
          {
            candidate_id: candidateId,
            template_id: templateId,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        )
          .catch((error) => {
            console.error(`Error creating standardized resume for candidate ${candidateId}:`, error);
            return null;
          })
      );

      // Trigger candidate-JD linking using jobdesc_id from job description API
      promises.push(
        api.post(
          `${BASE_URL}/api/candidate-jd`,
          {
            candidate_id: candidateId,
            jd_id: jobdescId, // jobdesc_id from job description API
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        )
          .catch((error) => {
            console.error(`Error linking candidate ${candidateId} to JD:`, error);
            return null;
          })
      );

      // Execute all API calls in parallel (non-blocking)
      await Promise.all(promises);
    } catch (error) {
      console.error("Error in single post-upload API calls:", error);
      // Don't block user flow on error
    }
  };

  // Trigger standardized resume and candidate-JD linking for all candidates after bulk upload
  const triggerBulkPostUploadAPIs = async (candidates) => {
    try {
      const token = localStorage.getItem("jwtToken");
      if (!token) {
        console.error("No JWT token found");
        return;
      }
      if (!jobId) {
        console.error("No jobId found");
        return;
      }

      // Fetch job description to get jobdesc_id
      const jobResponse = await api.get(`${BASE_URL}/api/job-desc/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const jobData = Array.isArray(jobResponse.data) ? jobResponse.data[0] : jobResponse.data;
      const jobdescId = jobData?.jobdesc_id;

      if (!jobdescId) {
        console.error("No jobdesc_id found in job description");
        return;
      }

      // Fetch template ID
      const templateResponse = await api.get(`${BASE_URL}/api/templates/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const templateId = templateResponse.data?._id || templateResponse.data?.template_id;
      
      if (!templateId) {
        console.error("No template ID found for standardized resume creation");
        return;
      }

      // Filter candidates that have valid candidate_id or lead_id
      // Only process candidates from new_candidates array
      const validCandidates = candidates.filter(candidate => 
        (candidate.candidate_id || candidate.lead_id) && 
        (candidate.candidate_id !== null || candidate.lead_id !== null)
      );

      if (validCandidates.length === 0) {
        console.error("No valid candidates with candidate_id or lead_id found");
        return;
      }

      // Trigger candidate-JD linking APIs first (independently, not waiting for standardized API)
      const candidateJdPromises = validCandidates.map(async (candidate) => {
        const candidateId = candidate.candidate_id || candidate.lead_id;

        return api.post(
          `${BASE_URL}/api/candidate-jd`,
          {
            candidate_id: candidateId,
            jd_id: jobdescId, // jobdesc_id from job description API
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        )
          .catch((error) => {
            console.error(`Error linking candidate ${candidateId} to JD:`, error);
            return null;
          });
      });

      // Execute candidate-JD API calls in parallel and wait for completion
      await Promise.all(candidateJdPromises);

      // After all candidate-jd API calls complete, trigger the candidates API
      // This is only for bulk upload in jobs page (when jobId exists)
      // Wait for this API to complete (success or failure) before returning (modal will close after this)
      try {
        await api.get(`${BASE_URL}/api/jd/${jobdescId}/candidates`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        console.error("Error fetching candidates after bulk upload:", error);
        // Still continue to allow modal to close even on error
      }

      // Fetch job-desc API (don't wait for this, it can run in background)
      try {
        await api.get(`${BASE_URL}/api/job-desc/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        console.error("Error fetching job description after bulk upload:", error);
        // Don't block user flow on error
      }

      // Trigger standardized resume creation APIs independently (don't wait for these)
      // These run in the background and don't block other APIs
      validCandidates.forEach((candidate) => {
        const candidateId = candidate.candidate_id || candidate.lead_id;

        api.post(
          `${BASE_URL}/api/resume/standardized`,
          {
            candidate_id: candidateId,
            template_id: templateId,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        )
          .catch((error) => {
            console.error(`Error creating standardized resume for candidate ${candidateId}:`, error);
            return null;
          });
      });
    } catch (error) {
      console.error("Error in bulk post-upload API calls:", error);
      // Don't block user flow on error
    }
  };

  // Bulk validation errors come back as errors[{index, field, code}]
  const getBulkUploadErrorMessage = (error, fallback) => {
    const rowErrors = error?.response?.data?.errors;
    if (Array.isArray(rowErrors) && rowErrors.length > 0) {
      return rowErrors
        .map(({ index, field, code }) => {
          const fileName = files[index]?.name || `File ${index + 1}`;
          const reason = code === "EMAIL_INVALID"
            ? "invalid email"
            : code?.endsWith("_REQUIRED")
              ? `${field || "value"} is required`
              : `${field ? `${field}: ` : ""}${code || "invalid"}`;
          return `${fileName}: ${reason}`;
        })
        .join("; ");
    }
    return error?.response?.data?.error || error?.response?.data?.message || fallback;
  };

  const submitEvaluation = async (
    mandatorySkills = [],
    preferredSkills = [],
    provider = 'freshteam',
    skills = [],
    flag_ref = 'add',
    min_exp = ''
  ) => {
    const emailError = invalidBulkEmail();
    if (emailError) {
      setCandidateModalError(emailError);
      return;
    }

    setIsProcessing(true);
    setIsSubmitting(true);

    const formData = new FormData();
    appendBulkCandidates(formData);

    try {
      const response = await api.post(
        `${BASE_URL}/api/candidates/bulk`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.status >= 200 && response.status < 300) {
        // Handle different response structures
        // New structure: {results: [...], success_count, error_count, total_count}
        // Old structure: {candidates: [...], new_candidates: [...]}
        const resultsArray = response.data?.results || [];
        const candidatesArray = response.data?.candidates || [];
        const newCandidatesArray = response.data?.new_candidates || [];
        
        // Get ALL candidates for display in the table
        // If results exist, use results; otherwise fall back to candidates
        const allCandidates = resultsArray.length > 0 ? resultsArray : candidatesArray;
        // Get new_candidates separately - ONLY for triggering APIs (standardized resume and candidate-JD)
        // If results exist and no explicit new_candidates, use results (they're all new in bulk upload)
        // Otherwise use new_candidates array
        const newCandidates = resultsArray.length > 0 && newCandidatesArray.length === 0 
          ? resultsArray 
          : newCandidatesArray;
        
        const failedFiles = response.data?.failed_files;
        if (failedFiles) {
          setCandidateModalError(failedFiles);
          setFiles([]);
          setIsProcessing(false);
          setTimeout(() => setIsSubmitting(false), 1500);
          return;
        }

        // Process new_candidates separately - ONLY for triggering APIs
        // Keep modal open with loading until /api/jd/id/candidates succeeds
        if (newCandidates?.length && jobId) {
          const sortedNewCandidates = newCandidates.sort((a, b) => (a.rank || 0) - (b.rank || 0));
          
          // Map new_candidates for API calls
          const mappedNewCandidates = sortedNewCandidates.map(candidate => {
            return {
              ...candidate,
              // Use candidate_id from new_candidates, fallback to lead_id if candidate_id is null
              candidate_id: candidate.candidate_id || candidate.lead_id,
              // Use candidate_id as lead_id if lead_id doesn't exist
              lead_id: candidate.lead_id || candidate.candidate_id,
              // Map name to first_name/last_name if needed
              first_name: candidate.first_name || (candidate.name ? candidate.name.split(' ')[0] : ''),
              last_name: candidate.last_name || (candidate.name ? candidate.name.split(' ').slice(1).join(' ') : ''),
              // Ensure numeric fields have defaults
              resume_exp: candidate.resume_exp ? Math.round(parseFloat(candidate.resume_exp)) : 0,
              jd_mapping_count: candidate.jd_mapping_count || 0,
              // Ensure array fields have defaults
              resume_skills: candidate.resume_skills || candidate.parsed_skills || candidate.skills || [],
              matched_skills: candidate.matched_skills || [],
              parsed_skills: candidate.parsed_skills || candidate.skills || [],
            };
          });
          
          // Filter new_candidates to only include those with valid candidate_id or lead_id for API calls
          const validNewCandidates = mappedNewCandidates.filter(candidate => 
            (candidate.candidate_id || candidate.lead_id) && 
            (candidate.candidate_id !== null || candidate.lead_id !== null)
          );
          
          // Trigger APIs and wait for /api/jd/id/candidates to succeed, then refresh and close modal
          // Keep submitting state active until modal closes
          if (validNewCandidates?.length) {
            triggerBulkPostUploadAPIs(validNewCandidates).then(async () => {
              // After /api/jd/id/candidates succeeds, refresh candidates
              await fetchCandidates();
              
              // Close modal after refresh (state will be reset in handleCloseTabbedUploadModal or elsewhere)
              setFiles([]);
              if (isTabbedUploadModalOpen) {
                handleCloseTabbedUploadModal();
              } else {
                setIsAddNewCandidateModalOpen(false);
              }
              setIsProcessing(false);
              setTimeout(() => setIsSubmitting(false), 1500);
            }).catch(async () => {
              // Even on error, refresh and close modal
              await fetchCandidates();
              setFiles([]);
              if (isTabbedUploadModalOpen) {
                handleCloseTabbedUploadModal();
              } else {
                setIsAddNewCandidateModalOpen(false);
              }
              setIsProcessing(false);
              setTimeout(() => setIsSubmitting(false), 1500);
            });
          } else {
            // No valid candidates, close modal immediately
            setFiles([]);
            if (isTabbedUploadModalOpen) {
              handleCloseTabbedUploadModal();
            } else {
              setIsAddNewCandidateModalOpen(false);
            }
            setIsProcessing(false);
            setTimeout(() => setIsSubmitting(false), 1500);
          }
        } else {
          // No new candidates or no jobId, close modal immediately
          setFiles([]);
          if (isTabbedUploadModalOpen) {
            handleCloseTabbedUploadModal();
          } else {
            setIsAddNewCandidateModalOpen(false);
          }
          setIsProcessing(false);
          setTimeout(() => setIsSubmitting(false), 1500);
        }
      }
    } catch (error) {
      const errorMsg = bulkUploadErrorMessage(error, files);

      const limitNotice = resumeLimitMessage(error);
      if (limitNotice) {
        if (isTabbedUploadModalOpen) {
          handleCloseTabbedUploadModal();
        } else {
        setIsAddNewCandidateModalOpen(false);
        }
        setResumeLimitNotice(limitNotice);
        setTimeout(() => setIsLimitReachedModalOpen(true), 300);
      } else {
        setCandidateModalError(errorMsg);
      }
      setIsProcessing(false);
      setTimeout(() => setIsSubmitting(false), 1500);
    }
  };

  useEffect(() => {
    const handleReevaluate = async () => {
      if (isModalConfirmed && jobId) {
        setIsProcessing(true);
        setIsSubmitting(true);
        
        try {
          // Fetch job description to get jobdesc_id
          const jobResponse = await api.get(`${BASE_URL}/api/job-desc/${jobId}`);
          const jobData = Array.isArray(jobResponse.data) ? jobResponse.data[0] : jobResponse.data;
          const jobdescId = jobData?.jobdesc_id;

          if (!jobdescId) {
            console.error("No jobdesc_id found in job description");
            setIsProcessing(false);
            setTimeout(() => setIsSubmitting(false), 1500);
            setIsModalConfirmed(false);
            return;
          }

          // Call re-evaluate API
          const response = await api.post(`${BASE_URL}/api/jd/${jobdescId}/reevaluate`);
          
          if (response.status === 200 || response.status === 201) {
            // Refresh candidates list after re-evaluation
            await fetchCandidates();
          }
        } catch (error) {
          const errorMsg = error?.response?.data?.error || error?.response?.data?.message || "Error re-evaluating candidates.";
          setCandidateModalError(errorMsg);
        } finally {
          setIsProcessing(false);
          setTimeout(() => setIsSubmitting(false), 1500);
          setIsModalConfirmed(false);
        }
      }
    };

    handleReevaluate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalConfirmed]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      setIsModalOpen(true);
      return;
    }
    else {
      const skillsArray = Array.isArray(parsedSkills) ? parsedSkills : [];
      const mandatorySkillsArray = Array.isArray(mandatorySkills) ? mandatorySkills : [];
      const preferredSkillsArray = Array.isArray(preferredSkills) ? preferredSkills : [];

      await submitEvaluation(mandatorySkillsArray, preferredSkillsArray, 'internal', skillsArray, 'add', jdMinExp);
    }
  };

  // Bulk upload handler for candidates page (when no jobId)
  const handleBulkUploadCandidates = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      setCandidateModalError("Please select at least one file to upload.");
      return;
    }

    const emailError = invalidBulkEmail();
    if (emailError) {
      setCandidateModalError(emailError);
      return;
    }

    setIsProcessing(true);
    setCandidateModalError("");

    const formData = new FormData();
    appendBulkCandidates(formData);

    try {
      const response = await api.post(
        `${BASE_URL}/api/candidates/bulk`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.status === 200 || response.status === 201) {
        // Refresh candidates list
        await fetchCandidates();
        
        setFiles([]);
        handleCloseTabbedUploadModal();
      }
    } catch (error) {
      const errorMsg = bulkUploadErrorMessage(error, files);
      const limitNotice = resumeLimitMessage(error);

      if (limitNotice) {
        handleCloseTabbedUploadModal();
        setResumeLimitNotice(limitNotice);
        setTimeout(() => setIsLimitReachedModalOpen(true), 300);
      } else {
        setCandidateModalError(errorMsg);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOpenResumeUploadModal = () => {
    setIsTabbedUploadModalOpen(true);
  };

  const handleCloseResumeUploadModal = () => {
    setFiles([]);
    setCandidateModalError("")
    setIsAddNewCandidateModalOpen(false);
  };

  const handleCloseTabbedUploadModal = () => {
    setIsTabbedUploadModalOpen(false);
    setFiles([]);
    setCandidateModalError("");
    setCandidateName("");
    setCandidateEmail("");
    setSelectedFile(null);
    setAddCandidateError("");
  };

  const getResumeUrl = (url) => {
    if (!url) return "";
    // If it's already a full URL, return it
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    // Otherwise, construct the full URL
    return `${BASE_URL}${url}`;
  };

  const handleCloseAddCandidateModal = () => {
    setIsAddCandidateModalOpen(false);
    setCandidateName("");
    setSelectedFile(null);
    setAddCandidateError("");
  };

  const handleAddCandidateFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (fileExtension === 'pdf' || fileExtension === 'doc' || fileExtension === 'docx') {
        setSelectedFile(file);
      } else {
        alert('Only PDF and DOC files are allowed.');
        e.target.value = '';
      }
    }
  };

  const handleAddCandidateFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (fileExtension === 'pdf' || fileExtension === 'doc' || fileExtension === 'docx') {
        setSelectedFile(file);
      } else {
        alert('Only PDF and DOC files are allowed.');
      }
    }
  };

  const handleAddCandidateFileDragOver = (e) => {
    e.preventDefault();
  };

  const handleUploadAndContinue = async () => {
    const trimmedName = candidateName.trim();
    if (!trimmedName) {
      setAddCandidateError("Candidate name is required");
      return;
    }

    if (!selectedFile) {
      return;
    }

    const trimmedEmail = candidateEmail.trim();
    const emailError = validateEmail(trimmedEmail);
    if (emailError) {
      setAddCandidateError(emailError);
      return;
    }

    setIsUploadingCandidate(true);
    setAddCandidateError("");

    const formData = new FormData();
    formData.append("name", trimmedName);
    formData.append("email", trimmedEmail);
    formData.append("resume_file", selectedFile);

    try {
      const response = await api.post(
        `${BASE_URL}/api/candidate`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.status === 200 || response.status === 201) {
        const newCandidate = response.data;
        
        if (newCandidate && newCandidate.candidate_id) {
          // If jobId exists (jobs/id page), trigger post-upload APIs and refresh candidates list
          if (jobId) {
            const mappedCandidate = {
              ...newCandidate,
              lead_id: newCandidate.candidate_id,
              candidate_id: newCandidate.candidate_id,
              first_name: newCandidate.name ? newCandidate.name.split(' ')[0] : '',
              last_name: newCandidate.name ? newCandidate.name.split(' ').slice(1).join(' ') : '',
              resume_skills: newCandidate.parsed_skills || [],
              matched_skills: newCandidate.matched_skills || [],
              parsed_skills: newCandidate.parsed_skills || [],
              email: newCandidate.email || trimmedEmail,
              jd_mapping_count: 0,
              resume_exp: newCandidate.resume_exp ? Math.round(parseFloat(newCandidate.resume_exp)) : 0,
              updated_at: new Date().toISOString(),
            };
            
            // Add candidate to list immediately at the first position (optimistic update)
            const updatedCandidates = [mappedCandidate, ...candidates];
            setCandidates(updatedCandidates);
            setFilteredCandidates(updatedCandidates);
            
            handleCloseTabbedUploadModal();
            
            const newCandidateId = newCandidate.candidate_id;
            
            triggerSinglePostUploadAPIs(newCandidate.candidate_id).then(() => {
              setTimeout(async () => {
                const fetchedCandidates = await fetchCandidates();
                if (fetchedCandidates && fetchedCandidates.length > 0) {
                  const newCandidateIndex = fetchedCandidates.findIndex(
                    c => (c.candidate_id === newCandidateId || c.lead_id === newCandidateId)
                  );
                  
                  if (newCandidateIndex > 0) {
                    const newCandidateData = fetchedCandidates[newCandidateIndex];
                    const otherCandidates = fetchedCandidates.filter(
                      (_, index) => index !== newCandidateIndex
                    );
                    const reorderedCandidates = [newCandidateData, ...otherCandidates];
                    setCandidates(reorderedCandidates);
                    setFilteredCandidates(reorderedCandidates);
                  } else if (newCandidateIndex === -1) {
                    setCandidates([mappedCandidate, ...fetchedCandidates]);
                    setFilteredCandidates([mappedCandidate, ...fetchedCandidates]);
                  }
                }
              }, 500);
            });
          } else {
            const mappedCandidate = {
              ...newCandidate,
              lead_id: newCandidate.candidate_id,
              candidate_id: newCandidate.candidate_id,
              first_name: newCandidate.name ? newCandidate.name.split(' ')[0] : '',
              last_name: newCandidate.name ? newCandidate.name.split(' ').slice(1).join(' ') : '',
              resume_skills: newCandidate.parsed_skills || [],
              email: newCandidate.email || trimmedEmail,
              jd_mapping_count: 0,
              updated_at: new Date().toISOString(),
            };
 
            const updatedCandidates = [mappedCandidate, ...candidates];
            setCandidates(updatedCandidates);
            setFilteredCandidates(updatedCandidates);
            
            handleCloseTabbedUploadModal();
            const previousRoute = jobId ? `/jobs/${jobId}` : '/candidates';
            navigate(`/candidate-list/${newCandidate.candidate_id}`, {
              state: { from: previousRoute, jobId: jobId ? job?.jobdesc_id || jobId : null }
            });
          }
        } else {
          if (jobId) {
            await fetchCandidates();
            handleCloseTabbedUploadModal();
          } else {
            handleCloseTabbedUploadModal();
            fetchCandidates();
          }
        }
      }
    } catch (error) {
      const data = error?.response?.data || {};
      const errorMsg = emailCodeMessage(data.code || data.error) ||
                       data.message ||
                       (typeof data.error === "string" ? data.error : "") ||
                       "Upload properly formatted resume.";
      const limitNotice = resumeLimitMessage(error);

      if (limitNotice) {
        handleCloseTabbedUploadModal();
        setResumeLimitNotice(limitNotice);
        setTimeout(() => setIsLimitReachedModalOpen(true), 300);
      } else {
        setAddCandidateError(errorMsg);
      }
    } finally {
      setIsUploadingCandidate(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    const newFiles = selectedFiles.filter(newFile =>
      !files.some(existingFile =>
        existingFile.name === newFile.name &&
        existingFile.size === newFile.size &&
        existingFile.lastModified === newFile.lastModified
      )
    );

    if (newFiles.length < selectedFiles.length) {
      setCandidateModalError("Cannot upload duplicate files.");
    }

    const allFiles = [...files, ...newFiles];

    if (allFiles.length > MAX_FILE_LIMIT) {
      setCandidateModalError(`You can only upload a maximum of ${MAX_FILE_LIMIT} files.`);
      return;
    }

    const invalidExtensions = [];
    const oversizedFiles = [];

    const validNewFiles = newFiles.filter((file) => {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowedFileTypes.includes(file.type)) {
        invalidExtensions.push(ext);
        return false;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        oversizedFiles.push(file.name);
        return false;
      }
      return true;
    });

    const uniqueInvalid = [...new Set(invalidExtensions)];
    const uniqueOversized = [...new Set(oversizedFiles)];

    if (uniqueInvalid.length > 0) {
      setIsEvaluateButtonEnabled(false);
      setCandidateModalError(`Invalid file types: ${uniqueInvalid.join(', ')}`)
    } else if (uniqueOversized.length > 0) {
      setIsEvaluateButtonEnabled(false);
      setCandidateModalError(`Remove oversized files. Max ${MAX_FILE_SIZE_BYTES / (1024 * 1024)}MB per file.`)
    } else {
      setIsEvaluateButtonEnabled(true);
    }

    setFiles((prev) => [...prev, ...validNewFiles]);
    setBulkDetails((prev) => {
      const next = { ...prev };
      validNewFiles.forEach((file) => {
        const key = bulkFileKey(file);
        if (!next[key]) next[key] = { name: "", email: "" };
      });
      return next;
    });
    e.target.value = '';
  };

  const handleBulkDetailChange = (file, field, value) => {
    const key = bulkFileKey(file);
    setBulkDetails((prev) => ({
      ...prev,
      [key]: {
        name: "",
        email: "",
        ...(prev[key] || {}),
        [field]: value,
      },
    }));
  };

  const invalidBulkEmail = () => {
    for (const file of files) {
      const detail = bulkDetails[bulkFileKey(file)] || { name: "", email: "" };
      if (!detail.name.trim()) return `Name is required for ${file.name}`;
      const emailError = validateEmail(detail.email.trim());
      if (emailError) return `${file.name}: ${emailError}`;
    }
    return "";
  };

  const appendBulkCandidates = (formData) => {
    const names = [];
    const emails = [];
    files.forEach((file) => {
      const detail = bulkDetails[bulkFileKey(file)] || { name: "", email: "" };
      formData.append("resume_files", file);
      names.push(detail.name.trim());
      emails.push(detail.email.trim());
    });
    formData.append("names", JSON.stringify(names));
    formData.append("emails", JSON.stringify(emails));
  };

  const handleRemoveFile = (fileName) => {
    setFiles((prevFiles) => {
      const fileIndex = prevFiles.findIndex((file) => file.name === fileName);
      if (fileIndex === -1) return prevFiles;
      const removed = prevFiles[fileIndex];
      setBulkDetails((prev) => {
        const next = { ...prev };
        delete next[bulkFileKey(removed)];
        return next;
      });

      const updatedFiles = [
        ...prevFiles.slice(0, fileIndex),
        ...prevFiles.slice(fileIndex + 1)
      ];

      if (updatedFiles.length === 0) {
        setIsEvaluateButtonEnabled(false);
        return updatedFiles;
      }

      const invalidFiles = updatedFiles.filter(
        (file) => !allowedFileTypes.includes(file.type)
      );
      const oversizedFiles = updatedFiles.filter((file) => file.size > MAX_FILE_SIZE_BYTES);

      const invalidExtensions = [...new Set(invalidFiles.map((file) =>
        file.name.split('.').pop().toLowerCase()
      ))];
      const oversizedFileNames = [...new Set(oversizedFiles.map((file) => file.name))];

      if (invalidExtensions.length === 0 && oversizedFileNames.length === 0) {
        setIsEvaluateButtonEnabled(true);
      } else {
        setIsEvaluateButtonEnabled(false);
        if (oversizedFileNames.length > 0) {
          setCandidateModalError(`Remove oversized files. Max ${MAX_FILE_SIZE_BYTES / (1024 * 1024)}MB per file.`)
        } else if (invalidExtensions.length > 0) {
          setCandidateModalError(`Remove invalid file types: ${invalidExtensions.join(", ")}`)
        }
      }
      return updatedFiles;
    });
  };
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!event.target.closest(".tooltip-container")) {
        setTooltipIndex(null);
      }
    };

    const handleScroll = () => {
      setTooltipIndex(null);
    };

    if (tooltipIndex !== null) {
      document.addEventListener("click", handleOutsideClick);
      window.addEventListener("scroll", handleScroll);
    }

    return () => {
      document.removeEventListener("click", handleOutsideClick);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [tooltipIndex]);

  const sortedCandidates = useMemo(() => {
    if (filteredCandidates.length === 0) return [];

    let filtered = filteredCandidates.filter((candidate) => {
      const search = searchQuery.toLowerCase();
      const candidateName = candidate.name?.toLowerCase() || 
                           `${candidate.first_name || ""} ${candidate.last_name || ""}`.toLowerCase();
      const matchesSearch =
        candidateName.includes(search) ||
        candidate.name?.toLowerCase().includes(search) ||
        candidate.first_name?.toLowerCase().includes(search) ||
        candidate.last_name?.toLowerCase().includes(search) ||
        candidate.job_title?.toLowerCase().includes(search) ||
        candidate.email?.toLowerCase().includes(search) ||
        (candidate.resume_skills || []).join(" ").toLowerCase().includes(search) ||
        (candidate.parsed_skills || []).join(" ").toLowerCase().includes(search) ||
        (candidate.matched_skills || []).join(" ").toLowerCase().includes(search);

      if (rankingFilter === "topStar") {
        return matchesSearch && calculateRankings.topStars.includes(candidate.lead_id);
      } else if (rankingFilter === "risingStar") {
        return matchesSearch && calculateRankings.risingStars.includes(candidate.lead_id);
      }

      return matchesSearch;
    });

    return [...filtered].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case "final_score":
          aValue = a.final_score;
          bValue = b.final_score;
          break;
        case "experience_relevance":
          aValue = a.resume_exp;
          bValue = b.resume_exp;
          break;
        case "skill_relevance":
          aValue = a.skill_relevance;
          bValue = b.skill_relevance;
          break;
        case "name":
          aValue = a.name?.toLowerCase() || a.first_name?.toLowerCase() || "";
          bValue = b.name?.toLowerCase() || b.first_name?.toLowerCase() || "";
          break;
        case "jd_mapping_count":
          aValue = a.jd_mapping_count || 0;
          bValue = b.jd_mapping_count || 0;
          break;
        case "updated_at":
          aValue = a.updated_at || "";
          bValue = b.updated_at || "";
          break;
        case "status":
          aValue = a.status?.toLowerCase() || "";
          bValue = b.status?.toLowerCase() || "";
          break;
        case "email":
          aValue = a.email?.toLowerCase() || "";
          bValue = b.email?.toLowerCase() || "";
          break;
        case "job_title":
          aValue = a.job_title?.toLowerCase() || "";
          bValue = b.job_title?.toLowerCase() || "";
          break;
        case "resume_skills":
          aValue = (a.resume_skills || []).length;
          bValue = (b.resume_skills || []).length;
          break;
        case "parsed_skills":
          aValue = (a.parsed_skills || []).length;
          bValue = (b.parsed_skills || []).length;
          break;
        case "matched_skills":
          aValue = (a.matched_skills || []).length;
          bValue = (b.matched_skills || []).length;
          break;
        case "resume_exp":
          aValue = a.resume_exp || 0;
          bValue = b.resume_exp || 0;
          break;
        default:
          aValue = a[sortField];
          bValue = b[sortField];
      }

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredCandidates, sortField, sortOrder, searchQuery, rankingFilter, calculateRankings]);

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(currentPage * rowsPerPage, sortedCandidates.length);

  const candidatesToDisplay = sortedCandidates.slice(startIndex, endIndex);
  const totalPages = Math.ceil(sortedCandidates.length / rowsPerPage);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages || 1);
    }
    // eslint-disable-next-line
  }, [totalPages]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setTimeout(() => {
      if (firstHeaderCellRef.current) {
        firstHeaderCellRef.current.focus();
      }
    }, 0);
  };

  useEffect(() => {
    if (!isCandidateLoading && atsId && jobId) {
      handleRefresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCandidateLoading]);

  const refreshZohoToken = async () => {
    try {
      const response = await api.post(`${BASE_URL}/api/zoho/refresh-token`);
      const newToken = response.data.access_token;

      localStorage.setItem("zoho_access_token", newToken);
      return newToken;
    } catch (error) {
      console.error("Error refreshing Zoho token:", error);
    }
  };

  const getCandidatesFromZoho = async (atsRefId) => {
    try {
      setIsSubmitting(true)
      const response = await api.post(
        `${BASE_URL}/api/zoho/candidates/process`,
        { zoho_token: localStorage.getItem("zoho_access_token"), ats_ref_id: atsRefId }
      );
      await submitEvaluation(mandatorySkills, preferredSkills, jdProvider, parsedSkills, 'add', jdMinExp)
      return response
    } catch (error) {
      console.error("Error fetching candidates or processing associate requests:", error);
      throw error;
    }
    finally {
      setIsSubmitting(false)
    }
  }

  const handleRefresh = async () => {
    const atsRefId = job?.ats_ref_id;
    if (!atsRefId) {
      return;
    }
    setIsSubmitting(true);
    setIsSyncing(true)

    let zohoAccessToken = localStorage.getItem("zoho_access_token");
    if (!zohoAccessToken) {
      console.error("Access token is not available.");
      setIsSubmitting(false);
      setIsSyncing(false)
      return;
    }

    try {
      await getCandidatesFromZoho(atsRefId);
    } catch (error) {
      if (error.response && error.response.status === 401 && error.response.data.code === "INVALID_TOKEN") {
        console.error("Access token is invalid, refreshing token...");
        try {
          await refreshZohoToken();

          await getCandidatesFromZoho(atsRefId);
        } catch (refreshError) {
          console.error("Error refreshing token and retrying request:", refreshError);
          setError("Unable to refresh the candidate!");
          setIsErrorModalOpen(true);
          setTimeout(() => {
            setIsErrorModalOpen(false);
          }, 5000);
        }
      } else {
        console.error("Error fetching data from Zoho API:", error);
        setError("Unable to refresh the candidate!");
        setIsErrorModalOpen(true);
        setTimeout(() => {
          setIsErrorModalOpen(false);
        }, 5000);
      }
    } finally {
      setIsSubmitting(false);
      setIsSyncing(false)
      setIsSyncComplete(true);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  return (
    <div className="flex flex-col w-full h-full"
      style={{ backgroundColor: theme.palette.primary.v2, color: theme.palette.text.main, borderColor: theme.palette.secondary.v4 }}
    >
      <GlobalLoader isLoading={isSubmitting} />
      <ErrorToast message={error} show={isErrorModalOpen} onClose={() => setIsErrorModalOpen(false)} />
      <div className={`
      flex flex-wrap ${jobId ? "max-lg:justify-center" : ""} 
       max-sm:gap-2 justify-between items-center px-4 sm:px-6 w-full py-5 border rounded-t
        ${!jobId ? "max-sm:pl-12" : 'border-b-0'}`
      }
        style={{ backgroundColor: theme.palette.primary.main, color: theme.palette.text.main, borderColor: theme.palette.secondary.v4 }}
      >
        <div>
          <span className={cn("font-semibold", !jobId && "text-2xl")}
            style={{ color: theme.palette.text.main }}>
            Candidates
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-80">
            <CiSearch
              style={{
                position: 'absolute',
                left: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: theme.palette.text.main,
                pointerEvents: 'none',
                fontSize: '16px',
              }}
            />
            <input
              type="text"
              placeholder={"Search job title or skills"}
              aria-label='Search job title or skills'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`border px-2 py-1 pl-8 text-sm rounded w-full focus:outline-none focus:ring-0 focus:border ${searchQuery ? 'pr-8' : ''}`}
              style={{
                borderColor: theme.palette.secondary.main,
                color: theme.palette.text.main,
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-accent-gray-medium hover:text-gray-700"
                aria-label="Clear search"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>
          {!jobId && (
            <Button
              variant="contained"
              {...uploadGate.controlProps}
              onClick={uploadGate.guard(() => setIsTabbedUploadModalOpen(true))}
              sx={{
                backgroundColor: theme.palette.info.main,
                color: theme.palette.text.light,
                textTransform: "none",
                "&.Mui-disabled": {
                  backgroundColor: theme.palette.secondary.v3,
                  color: theme.palette.text.light,
                },
              }}
            >
              Add Candidate
            </Button>
          )}
          {!jobId && uploadGate.hint}
          {jobId && (
            <FormControl
              size="small"
              sx={{
                minWidth: 140,
                backgroundColor: theme.palette.primary.main,
                borderRadius: '4px',
                border: `1px solid ${theme.palette.secondary.main}`,
                '& .MuiInputBase-root': {
                  height: '30px',
                  paddingTop: '4px',
                  paddingBottom: '4px',
                  paddingLeft: '10px',
                  paddingRight: '10px',
                  boxSizing: 'border-box',
                  color: theme.palette.text.main,
                  [theme.breakpoints.down('md')]: {
                    fontSize: '14px',
                  },
                },
                '& .MuiSelect-select': {
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiSelect-icon': {
                  color: theme.palette.text.main,
                },
                '& fieldset': {
                  border: 'none',
                },
              }}
            >
              <Select
                value={rankingFilter}
                onChange={(e) => setRankingFilter(e.target.value)}
                onOpen={() => setRankingOpen(true)}
                onClose={() => setRankingOpen(false)}
                displayEmpty
                size="small"
                id="ranking-filter"
                inputProps={{
                  'aria-label': 'Filter candidates by ranking',
                  'aria-describedby': 'ranking-filter-description',
                  'aria-expanded': rankingOpen,
                }}
              >
                <MenuItem value="all" aria-label="Show all rankings">All Rankings</MenuItem>
                <MenuItem value="topStar" aria-label="Show only top star candidates">Top Star</MenuItem>
                <MenuItem value="risingStar" aria-label="Show only rising star candidates">Rising Star</MenuItem>
              </Select>
              <Typography id="ranking-filter-description" className="sr-only">
                Opens a dropdown to filter candidates by ranking category.
              </Typography>
            </FormControl>
          )}
          {jdProvider && jdProvider !== "internal" && (
            <Button
              onClick={() => submitEvaluation(mandatorySkills, preferredSkills, jdProvider, parsedSkills, 'add', jdMinExp)}
              disabled={isSyncing}
              sx={{
                backgroundColor: '#fff',
                color: theme.palette.text.main,
                border: '1px solid',
                borderColor: theme.palette.secondary.main,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                boxShadow: 'none',
                paddingTop: 0,
                paddingBottom: 0,
                '&.Mui-disabled': {
                  backgroundColor: '#fff',
                  color: '#9CA3AF',
                  borderColor: '#D1D5DB',
                  cursor: 'not-allowed',
                },
              }}
            >
              Refresh
            </Button>
          )}

          {jobId && (
            <>
              {selectedCandidates.length !== 0 &&
                <Button
                  onClick={handleBulkUpdate}
                  disabled={selectedCandidates.length === 0 || isUpdating || jdStatus === 'archived'}
                  sx={{
                    backgroundColor: '#fff',
                    color:
                      selectedCandidates.length === 0 || isUpdating || jdStatus === 'archived'
                        ? '#9CA3AF'
                        : theme.palette.text.main,
                    border: '1px solid',
                    borderColor:
                      selectedCandidates.length === 0 || isUpdating || jdStatus === 'archived'
                        ? '#D1D5DB'
                        : theme.palette.secondary.main,
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:disabled': {
                      backgroundColor: theme.palette.secondary.v3,
                      color: theme.palette.text.light,
                      borderColor: '#D1D5DB',
                    },
                  }}
                >
                  {isUpdating ? 'Updating...' : 'Archive'}
                </Button>
              }
              <Button
                onClick={async () => {
                  if (!jobId) return;
                  
                  setIsProcessing(true);
                  setIsSubmitting(true);
                  
                  try {
                    // Fetch job description to get jobdesc_id
                    const jobResponse = await api.get(`${BASE_URL}/api/job-desc/${jobId}`);
                    const jobData = Array.isArray(jobResponse.data) ? jobResponse.data[0] : jobResponse.data;
                    const jobdescId = jobData?.jobdesc_id;

                    if (!jobdescId) {
                      console.error("No jobdesc_id found in job description");
                      setIsProcessing(false);
                      setTimeout(() => setIsSubmitting(false), 1500);
                      return;
                    }

                    // Call re-evaluate API
                    const response = await api.post(`${BASE_URL}/api/jd/${jobdescId}/reevaluate`);
                    
                    if (response.status === 200 || response.status === 201) {
                      // Refresh candidates list after re-evaluation
                      await fetchCandidates();
                    }
                  } catch (error) {
                    const errorMsg = error?.response?.data?.error || error?.response?.data?.message || "Error re-evaluating candidates.";
                    setCandidateModalError(errorMsg);
                  } finally {
                    setIsProcessing(false);
                    setTimeout(() => setIsSubmitting(false), 1500);
                  }
                }}
                disabled={candidates.length === 0 || jdStatus === 'archived' || isSyncing || isProcessing}
                sx={{
                  backgroundColor:
                    candidates.length === 0 || jdStatus === 'archived' || isSyncing
                      ? '#4B5563'
                      : theme.palette.primary.main,
                  color:
                    candidates.length === 0 || jdStatus === 'archived' || isSyncing
                      ? '#9CA3AF'
                      : theme.palette.text.main,
                  border: '1px solid',
                  borderColor: candidates.length === 0 || jdStatus === 'archived' || isSyncing ? '#D1D5DB' : theme.palette.secondary.main,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.secondary.v3,
                    color: theme.palette.text.light,
                    cursor: 'not-allowed',
                  },
                }}
              >
                Re-evaluate
              </Button>
              <Button
                variant="contained"
                size="small"
                {...uploadGate.controlProps}
                onClick={uploadGate.guard(handleOpenResumeUploadModal)}
                disabled={jdStatus === 'archived' || isSyncing}
                type="button"
                sx={{
                  backgroundColor: jdStatus === 'archived' || isSyncing ? '#4B5563' : theme.palette.info.main,
                  color: jdStatus === 'archived' || isSyncing ? '#9CA3AF' : theme.palette.text.light,
                  textTransform: 'none',
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.secondary.v3,
                    color: theme.palette.text.light,
                    cursor: 'not-allowed',
                  },
                }}
              >
                Add
              </Button>
              {uploadGate.hint}

              {tblErrorMessage && (
                <p className="mt-2 text-sm text-accent-red">{tblErrorMessage}</p>
              )}

            </>
          )}
        </div>
      </div>
      {isCandidateLoading && candidates.length === 0 ? (
        <div className="mx-auto px-6 sm:px-12 lg:px-16 text-center mt-16">
          <div className="spinner-border animate-spin inline-block w-8 h-8 border-2 border-t-4 border-black rounded-full" />
          <p>Loading relevant candidates...</p>
        </div>
      ) : (
        <>
          <div className={`overflow-auto min-w-0 border rounded-b ${jobId ? '' : "m-6 max-md:mt-4 max-md:mx-4 mb-0 rounded"}`} style={{ color: theme.palette.text.main, borderColor: theme.palette.secondary.v4 }}>
            <div className='min-w-full min-h-full rounded' style={{ backgroundColor: theme.palette.primary.v3, borderColor: theme.palette.secondary.v4 }}>
              {!isCandidateLoading && candidates.length === 0 ? (
                <div
                  className="flex flex-col items-center justify-center h-64"
                  style={{ borderColor: theme.palette.secondary.v4, color: theme.palette.secondary.v7 }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-16 h-16 mb-4"
                    style={{ color: theme.palette.secondary.v7 }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  <p className="text-lg font-medium mb-1">
                    {job?.ats_ref_id ? (
                      isSyncComplete ? "No candidates found" : "Syncing candidates"
                    ) : (
                      "No candidates added yet"
                    )}
                  </p>
                  <p className="text-sm max-w-md text-center px-6" style={{ color: theme.palette.secondary.v7 }}>
                    {job?.ats_ref_id ? (
                      isSyncComplete ? (
                        "Try refreshing the page or syncing again to fetch candidates."
                      ) : (
                        "Please wait while we sync candidate data for this job."
                      )
                    ) : (
                      jobId ? "You can manually add candidates to this job description." : "You can manually add candidates job description."
                    )}
                  </p>
                </div>

              ) : (
                <Table stickyHeader={!jobId || screenWidth >= 768} className="w-full min-w-max border-0" sx={{ backgroundColor: theme.palette.primary.main, borderColor: theme.palette.secondary.v4 }}>
                  <TableHead
                    sx={{
                      backgroundColor: theme.palette.secondary.v5,
                      color: theme.palette.text.main,
                      zIndex: 10
                    }}
                  >
                    <TableRow>
                      {jobId && (
                        <TableCell
                          ref={firstHeaderCellRef}
                          tabIndex={0}
                          sx={{
                            width: widths.checkbox,
                            paddingLeft: 3,
                            backgroundColor: theme.palette.secondary.v5,
                            color: theme.palette.text.main,
                            zIndex: 10
                          }}
                        >
                          <input
                            type="checkbox"
                            className="w-4 h-4"
                            aria-label="Select All"
                            checked={selectedCandidates.length === filteredCandidates.length && filteredCandidates.length > 0}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                          />
                        </TableCell>
                      )}

                      <TableCell
                        ref={!jobId ? firstHeaderCellRef : null}
                        tabIndex={0}
                        sx={{
                          width: widths.name,
                          minWidth: widths.name,
                          paddingLeft: jobId ? 0 : 3,
                          backgroundColor: theme.palette.secondary.v5,
                          color: theme.palette.text.main,
                          zIndex: 10
                        }}
                      >
                        <TableSortLabel
                          active={sortField === "name"}
                          direction={sortField === "name" ? sortOrder : "asc"}
                          onClick={() => handleSort("name")}
                        >
                          <p className="flex flex-col">
                            Name
                            {jobId &&
                              <span className="text-xs italic">
                                Name of the candidate
                              </span>}
                          </p>
                        </TableSortLabel>
                      </TableCell>


                      {jobId && (
                        <TableCell
                          sx={{
                            width: widths.skills,
                            backgroundColor: theme.palette.secondary.v5,
                            color: theme.palette.text.main,
                            zIndex: 10
                          }}
                        >
                          <TableSortLabel
                            active={sortField === "matched_skills"}
                            direction={sortField === "matched_skills" ? sortOrder : "asc"}
                            onClick={() => handleSort("matched_skills")}
                          >
                            <p className="flex flex-col pl-2">
                              Matched Skills
                              <span className="text-xs italic">
                                (Matched Skills / Total Expected Skills)
                              </span>
                            </p>
                          </TableSortLabel>
                        </TableCell>
                      )}

                      {jobId && (
                        <>
                          <TableCell
                            sx={{
                              width: widths.expScore,
                              textAlign: 'start',
                              backgroundColor: theme.palette.secondary.v5,
                              color: theme.palette.text.main,
                              zIndex: 10
                            }}
                          >
                            <TableSortLabel
                              active={sortField === "experience_relevance"}
                              direction={sortField === "experience_relevance" ? sortOrder : "asc"}
                              onClick={() => handleSort("experience_relevance")}
                            >
                              <p className="flex flex-col">
                                Relevant Experience
                                <span className="text-xs italic">
                                  (Relevant Yrs. of Exp. / Total Expected Yrs. of Exp.)
                                </span>
                              </p>
                            </TableSortLabel>
                          </TableCell>

                          <TableCell
                            sx={{
                              width: widths.overallScore,
                              textAlign: 'start',
                              backgroundColor: theme.palette.secondary.v5,
                              color: theme.palette.text.main,
                              zIndex: 10
                            }}
                          >
                            <TableSortLabel
                              active={sortField === "final_score"}
                              direction={sortField === "final_score" ? sortOrder : "asc"}
                              onClick={() => handleSort("final_score")}
                            >
                              <p className="flex flex-col">
                                Score
                                <span className="text-xs italic">
                                  Sift Hire Score / 10
                                </span>
                              </p>
                            </TableSortLabel>
                          </TableCell>
                        </>
                      )}

                      {!jobId && (
                        <>
                          <TableCell
                            sx={{
                              width: widths.email,
                              backgroundColor: theme.palette.secondary.v5,
                              color: theme.palette.text.main,
                              zIndex: 10,
                            }}
                          >
                            <TableSortLabel
                              active={sortField === "email"}
                              direction={sortField === "email" ? sortOrder : "asc"}
                              onClick={() => handleSort("email")}
                            >
                              Email
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: widths.parsedSkills,
                              paddingLeft: "8px",
                              backgroundColor: theme.palette.secondary.v5,
                              color: theme.palette.text.main,
                              zIndex: 10,
                            }}
                          >
                            <TableSortLabel
                              active={sortField === "parsed_skills"}
                              direction={sortField === "parsed_skills" ? sortOrder : "asc"}
                              onClick={() => handleSort("parsed_skills")}
                            >
                              Parsed Skills
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: widths.jdResumes,
                              backgroundColor: theme.palette.secondary.v5,
                              color: theme.palette.text.main,
                              zIndex: 10,
                            }}
                          >
                            <TableSortLabel
                              active={sortField === "jd_mapping_count"}
                              direction={sortField === "jd_mapping_count" ? sortOrder : "asc"}
                              onClick={() => handleSort("jd_mapping_count")}
                            >
                              JD Resumes
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: widths.lastUpdated,
                              paddingRight: 3,
                              backgroundColor: theme.palette.secondary.v5,
                              color: theme.palette.text.main,
                              zIndex: 10,
                            }}
                          >
                            <TableSortLabel
                              active={sortField === "updated_at"}
                              direction={sortField === "updated_at" ? sortOrder : "asc"}
                              onClick={() => handleSort("updated_at")}
                            >
                              Last Updated
                            </TableSortLabel>
                          </TableCell>
                        </>
                      )}
                      {jobId && (
                        <TableCell
                          sx={{
                            width: widths.status,
                            textAlign: 'center',
                            backgroundColor: theme.palette.secondary.v5,
                            color: theme.palette.text.main,
                            zIndex: 10,
                          }}
                        >
                          <TableSortLabel
                            active={sortField === "status"}
                            direction={sortField === "status" ? sortOrder : "asc"}
                            onClick={() => handleSort("status")}
                          >
                            <p className="flex flex-col">
                              Status
                              <span className="text-xs italic">
                                Candidate Status
                              </span>
                            </p>
                          </TableSortLabel>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {candidatesToDisplay.length > 0 && candidatesToDisplay.map((candidate) => (
                      <TableRow
                        onClick={() => {
                          const previousRoute = jobId ? `/jobs/${jobId}` : '/candidates';
                          navigate(`/candidate-list/${candidate.candidate_id || candidate.id || candidate.lead_id}`, {
                            state: {
                              from: previousRoute,
                              jobId: jobId ? job?.jobdesc_id || jobId : candidate.job_id || candidate.jd_id || null,
                              jobTitle: job?.title || candidate.job_title || null,
                            }
                          });
                        }}
                        key={candidate.candidate_id || candidate.lead_id}
                        data-key={candidate.candidate_id || candidate.id}
                        className="border-gray-600 border-b hover:cursor-pointer hover:bg-gray-50 focus:outline focus:outline-2 focus:outline-accent-blue focus:outline-offset-2 rounded-sm"
                        onKeyDown={async (e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && e.currentTarget === e.target) {
                            e.preventDefault();
                            const previousRoute = jobId ? `/jobs/${jobId}` : '/candidates';
                            navigate(`/candidate-list/${candidate.candidate_id || candidate.id || candidate.lead_id}`, {
                              state: {
                                from: previousRoute,
                                jobId: jobId ? job?.jobdesc_id || jobId : candidate.job_id || candidate.jd_id || null,
                                jobTitle: job?.title || candidate.job_title || null,
                              }
                            });
                          }
                        }}
                        tabIndex={0}
                        aria-label={`View candidate details`}
                      >
                        {jobId && (
                          <TableCell sx={{ width: widths.checkbox, paddingLeft: 3 }}>
                            <input
                              type="checkbox"
                              aria-label="Select name"
                              className="w-4 h-4"
                              checked={selectedCandidates.includes(candidate.lead_id)}
                              onChange={() => handleSelectCandidate(candidate.lead_id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                        )}
                        <TableCell
                          sx={{
                            width: widths.name,
                            minWidth: widths.name,
                            paddingLeft: jobId ? 0 : 3,
                            paddingRight: "4px",
                            maxWidth: widths.name,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          }}
                        >
                          {(() => {
                            const candidateName = candidate.name || 
                              (candidate.first_name ? `${candidate.first_name} ${candidate?.last_name || ''}`.trim() : null);
                            
                            return candidateName ? (
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography
                                  sx={{
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-all',
                                    overflowWrap: 'break-word',
                                  }}
                                >
                                  {candidateName}
                                </Typography>

                                <span className="flex gap-1">
                                  {jobId && calculateRankings.topStars.includes(candidate.lead_id || candidate.candidate_id) && (
                                    <Tooltip title="Top Star" arrow>
                                      <div role="img" aria-label="Top Star">
                                        <TopStarIcon
                                          className="h-5 w-5"
                                          style={{ flexShrink: 0, color: theme.palette.text.orangeIcon }}
                                          aria-hidden="true"
                                        />
                                      </div>
                                    </Tooltip>
                                  )}

                                  {jobId && calculateRankings.risingStars.includes(candidate.lead_id || candidate.candidate_id) && (
                                    <Tooltip title="Rising Star" arrow>
                                      <div role="img" aria-label="Rising Star">
                                        <RisingStar
                                          className="h-5 w-5"
                                          style={{ flexShrink: 0, color: theme.palette.text.blueIcon }}
                                          aria-hidden="true"
                                        />
                                      </div>
                                    </Tooltip>
                                  )}
                                </span>
                              </Box>
                            ) : (
                              <Typography component="span" sx={{ color: theme.palette.text.main }}>
                                No name detected
                              </Typography>
                            );
                          })()}
                        </TableCell>

                        {!jobId && (
                          <>
                            <TableCell sx={{ width: widths.email, minWidth: widths.email, paddingRight: 2, whiteSpace: "nowrap" }}>
                              <Typography sx={{ color: candidate.email ? theme.palette.text.main : theme.palette.text.secondary }}>
                                {candidate.email || "No email"}
                              </Typography>
                            </TableCell>
                            <SkillChipsPopover
                              width={widths.parsedSkills}
                              skills={candidate.parsed_skills || []}
                            />
                            <TableCell sx={{ width: widths.jdResumes, minWidth: widths.jdResumes, whiteSpace: "nowrap" }}>
                              <Typography sx={{ color: theme.palette.text.main }}>
                                {candidate.jd_mapping_count || 0} {((candidate.jd_mapping_count || 0) === 0 || (candidate.jd_mapping_count || 0) === 1) ? 'resume' : 'resumes'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ width: widths.lastUpdated, minWidth: widths.lastUpdated, paddingRight: 3, whiteSpace: "nowrap" }}>
                              <Typography sx={{ color: theme.palette.text.main }}>
                                {(() => {
                                  if (!candidate.updated_at) return "N/A";
                                  try {
                                    const date = new Date(candidate.updated_at);
                                    return date.toLocaleDateString('en-US', { 
                                      year: 'numeric', 
                                      month: 'short', 
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    });
                                  } catch (error) {
                                    return candidate.updated_at;
                                  }
                                })()}
                              </Typography>
                            </TableCell>
                          </>
                        )}

                        {jobId && (
                          <SkillChipsPopover
                            width={widths.skills}
                            skills={candidate.matched_skills}
                            isAnalyticLabel={true}
                            totalSkills={mandatorySkills.length + preferredSkills.length}
                          />
                        )}

                        {jobId && (
                          <>
                            <TableCell sx={{ width: widths.expScore, textAlign: 'start' }}>
                              <Box
                                display="flex"
                                flexWrap="wrap"
                                alignItems="center"
                                gap={1}
                              >
                                <p className="w-12">{parseInt(candidate.resume_exp || 0)}/{jobExperience}</p>
                                <Chip
                                  label={`${parseInt(candidate.total_exp || 0)} Years`}
                                  size="small"
                                  sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.text.main,
                                    border: `1px solid ${theme.palette.secondary.main}`,
                                    cursor: 'pointer',
                                    '& .MuiChip-label': {
                                      padding: '0 10px',
                                    },
                                  }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell sx={{ width: widths.overallScore, textAlign: 'start' }}>
                              {parseFloat(candidate.final_score / 10).toFixed(1)}
                            </TableCell>
                          </>
                        )}
                        {jobId && (
                          <TableCell sx={{ width: widths.status, textAlign: 'center' }}>
                            <CandidateStatusToggle
                              candidate={candidate}
                              jobId={job?.jobdesc_id || jobId}
                              refreshData={fetchCandidates}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
          {candidates && candidates.length !== 0 && (
            <div
              className={cn("mt-auto w-full flex flex-col justify-center items-center px-3 py-3 sm:px-6", jobId && "px-0 sm:px-0")}
              style={{ backgroundColor: theme.palette.primary.v2 }}
            >
              <div className="relative flex max-lg:flex-col w-full justify-center items-center">
                <RowsPerPageSelector rowsPerPage={rowsPerPage} handleChangeRowsPerPage={handleChangeRowsPerPage} />
                <nav
                  aria-label="Pagination"
                  className="flex flex-wrap justify-center items-center gap-2"
                >
                  <button
                    onClick={() => handlePageChange(currentPage > 1 ? currentPage - 1 : 1)}
                    className={`relative inline-flex items-center border rounded px-2 py-2 text-white-400 bg-white hover:text-white hover:bg-accent-blue z-10 focus:outline-offset-0 text-xs sm:text-sm flex-shrink-0 ${currentPage === 1 || filteredCandidates.length === 0
                      ? 'text-accent-gray hover:text-accent-gray cursor-not-allowed'
                      : ''
                      }`}
                    disabled={currentPage === 1 || filteredCandidates.length === 0}
                    style={{ borderColor: theme.palette.secondary.v4 }}
                  >
                    <ChevronLeftIcon aria-hidden="true" className="h-5 w-5 text-white-400" />
                    <span className="hidden xs:inline ml-1">Prev</span>
                  </button>

                  <PaginationButtons
                    currentPage={currentPage}
                    totalPages={totalPages}
                    handlePageChange={handlePageChange}
                    theme={theme}
                  />

                  <button
                    onClick={() => handlePageChange(currentPage < totalPages ? currentPage + 1 : totalPages)}
                    className={`relative inline-flex items-center border rounded px-2 py-2 text-white-400 bg-white hover:bg-accent-blue hover:text-white z-10 focus:outline-offset-0 text-xs sm:text-sm flex-shrink-0 ${currentPage === totalPages || filteredCandidates.length === 0
                      ? 'text-accent-gray hover:text-accent-gray cursor-not-allowed'
                      : ''
                      }`}
                    disabled={currentPage === totalPages || filteredCandidates.length === 0}
                    style={{ borderColor: theme.palette.secondary.v4 }}
                  >
                    <span className="hidden xs:inline mr-1">Next</span>
                    <ChevronRightIcon aria-hidden="true" className="h-5 w-5 text-white-400" />
                  </button>
                </nav>
              </div>
            </div>
          )}
        </>
      )
      }
      {

        resumeUrl && createPortal(
          <div className="fixed inset-0 flex items-start justify-center bg-black bg-opacity-70 z-50">
            <div
              ref={resumeModalRef}
              className="relative p-4 pt-10 rounded-lg w-47rem h-3/4 mt-[70px] max-sm:mx-2"
              style={{ backgroundColor: theme.palette.primary.main }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="resume-viewer-title"
            >
              <button
                onClick={() => setResumeUrl(null)}
                aria-label="Close pop-up"
                className="absolute top-2 right-5 bg-black text-white rounded-full w-6 h-6 flex items-center justify-center transition-all duration-200 ease-in-out transform hover:scale-105"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
              <ResumeViewer
                fetchCandidates={fetchCandidates}
                candidate={candidate}
                theme={theme}
              />

              <iframe src={getResumeUrl(resumeUrl)} className="w-full h-full max-w-47rem" title="User resume preview"></iframe>
            </div>
          </div>,
          document.body
        )
      }
      {/* Tabbed Upload Modal */}
      <TabbedUploadModal
        isOpen={isTabbedUploadModalOpen}
        onClose={handleCloseTabbedUploadModal}
        candidateName={candidateName}
        setCandidateName={setCandidateName}
        candidateEmail={candidateEmail}
        setCandidateEmail={setCandidateEmail}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        isUploadingCandidate={isUploadingCandidate}
        addCandidateError={addCandidateError}
        handleAddCandidateFileChange={handleAddCandidateFileChange}
        handleAddCandidateFileDrop={handleAddCandidateFileDrop}
        handleAddCandidateFileDragOver={handleAddCandidateFileDragOver}
        handleUploadAndContinue={handleUploadAndContinue}
        files={files}
        bulkDetails={bulkDetails}
        handleBulkDetailChange={handleBulkDetailChange}
        handleFileChange={handleFileChange}
        handleRemoveFile={handleRemoveFile}
        isProcessing={isProcessing}
        candidateModalError={candidateModalError}
        isEvaluateButtonEnabled={isEvaluateButtonEnabled}
        handleUpload={jobId ? handleUpload : handleBulkUploadCandidates}
        onClearBulkError={() => setCandidateModalError("")}
        onClearSingleError={() => setAddCandidateError("")}
      />
      {
        isAddNewCandidateModalOpen && (
          <div className="fixed inset-0 flex items-start justify-center z-50 bg-black bg-opacity-70">
            <div className="w-full flex justify-center items-start mt-[70px]">
              <div
                ref={addCandidateModalRef}
                className="modal-content relative w-full max-w-47rem bg-white rounded-lg shadow-xl max-h-[90vh] flex flex-col overflow-hidden"
                role="dialog"
                aria-modal="true"
                aria-labelledby="add-candidate-title"
              >
                <div
                  className="modal-header flex justify-between items-center py-4 px-5 border-b"
                  style={{
                    backgroundColor: theme.palette.primary.main,
                    borderColor: theme.palette.secondary.v4,
                  }}
                >
                  <h2 className="font-semibold">Add New Candidate</h2>
                  <button
                    onClick={handleCloseResumeUploadModal}
                    aria-label="Close pop-up"
                    className="inline-flex items-center justify-center rounded-full bg-black text-white h-6 w-6"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>

                <div className="flex flex-col px-5 sm:px-14 py-9 gap-4 flex-grow overflow-hidden">
                  <div className="flex flex-col overflow-y-auto flex-grow gap-4">
                    <div className="flex flex-col sm:flex-row items-center justify-between">
                      <div className="w-full relative">
                        <input
                          type="file"
                          multiple
                          id="fileInput"
                          onChange={handleFileChange}
                          className="opacity-0 absolute top-0"
                          accept=".pdf, .docx"
                        />
                        <label
                          htmlFor="fileInput"
                          className="flex items-center justify-center rounded-lg py-2 px-4 cursor-pointer"
                          style={{
                            backgroundColor: theme.palette.info.main,
                            color: theme.palette.text.light,
                          }}
                        >
                          <BsCloudUpload className="mr-2 text-xl" aria-hidden="true" />
                          {files.length > 0 ? `${files.length} ${files.length === 1 ? "File Selected" : "Files Selected"}` : "Choose Resumes"}
                        </label>
                      </div>

                      <div className="w-full text-sm text-center sm:text-left">
                        <span className="w-full text-center inline-block">(Allowed: .pdf, .docx. Max {MAX_FILE_LIMIT} files)</span>
                      </div>
                    </div>

                    {files.length > 0 && (
                      <div className="mt-2">
                        <ul className="space-y-2">
                          {files.map((file) => {
                            const fileSize = formatFileSize(file.size);
                            return (
                              <li
                                key={`${file.name}-${file.size}`}
                                className="flex justify-between items-center p-2 rounded-lg shadow-sm border border-gray-600"
                              >
                                <span className="truncate">
                                  {truncateFilename(file.name)}
                                  <span className="ml-1 text-accent-gray-medium">({fileSize})</span>
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveFile(file.name)}
                                  className="text-red-600 hover:text-red-800 transition-all px-2"
                                >
                                  <span className="text-2xl leading-none">&times;</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                {candidateModalError && (
                  <div className="m-4 mt-0 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                    {candidateModalError}
                  </div>
                )}
                <div className="w-full py-3 px-5 flex justify-end border-t gap-2" style={{ borderColor: theme.palette.secondary.v4 }}>
                  <Button
                    onClick={handleUpload}
                    disabled={!isEvaluateButtonEnabled || isProcessing}
                    sx={{
                      backgroundColor: theme.palette.info.main,
                      textTransform: "none",
                      color: theme.palette.text.light,
                      '&.Mui-disabled': {
                        backgroundColor: theme.palette.secondary.v3,
                        color: theme.palette.text.dark,
                      },
                    }}
                  >
                    {isProcessing ? (
                      <span className="flex gap-1 text-black items-center">
                        <div className="animate-spin rounded-full border-t-2 border-black h-4 w-4 mr-2"></div>
                        Loading
                      </span>
                    ) : (
                      <span className="text-white font-medium flex gap-1 items-center">
                        Submit
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 12 11"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6.00008 0.164062L5.06008 1.10406L8.78008 4.83073H0.666748V6.16406H8.78008L5.06008 9.89073L6.00008 10.8307L11.3334 5.4974L6.00008 0.164062Z"
                            fill="white"
                          />
                        </svg>
                      </span>
                    )}
                  </Button>
                </div>

                {isModalOpen && (
                  <div className="fixed inset-0 flex items-start justify-center bg-gray-800 bg-opacity-50 z-50">
                    <div
                      ref={reEvaluateModalRef}
                      className="bg-white rounded-lg shadow-lg p-4 w-[90vw] max-w-sm mt-[70px]"
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby="re-evaluate-title"
                    >
                      <h3 className="text-xl font-semibold text-gray-800">Would you like to re-evaluate?</h3>
                      <p className="mt-2 text-accent-gray-dark">
                        {checkedSkills.length !== 0
                          ? "You have not selected any files. Would you like to re-evaluate existing candidates?"
                          : "You have not selected any skills. Would you like to re-evaluate existing candidates?"}
                      </p>
                      <div className="mt-4 flex justify-end gap-2">
                        <button
                          onClick={handlePopUpModalClose}
                          className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleModalConfirm}
                          className="bg-accent-blue px-4 py-2 rounded-lg hover:bg-accent-blue-hover text-white"
                        >
                          Yes, Continue
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

        )
      }
      <LimitReachedModal
        isLimitReachedModalOpen={isLimitReachedModalOpen}
        setIsLimitReachedModalOpen={setIsLimitReachedModalOpen}
        message={resumeLimitNotice}
      />

      {/* Add Candidate Modal */}
      {isAddCandidateModalOpen && (
        <div
          className="fixed inset-0 flex items-start justify-center z-50 bg-black bg-opacity-70"
          onClick={handleCloseAddCandidateModal}
        >
          <div className="rounded-lg w-full max-w-47rem overflow-hidden relative flex flex-col justify-between max-sm:mx-2 my-auto">
            <div
              ref={addCandidateListModalRef}
              className="modal-content relative w-full bg-white rounded-lg shadow-xl flex flex-col overflow-hidden"
              role="dialog"
              aria-modal="true"
              aria-labelledby="add-candidate-title"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div
                className="flex justify-between items-center py-4 px-5 border-b"
                style={{
                  backgroundColor: theme.palette.primary.main,
                  borderColor: theme.palette.secondary.v4,
                }}
              >
                <h2 id="add-candidate-title" className="font-semibold text-lg">
                  Add Candidate
                </h2>
                <button
                  onClick={handleCloseAddCandidateModal}
                  aria-label="Close modal"
                  className="inline-flex items-center justify-center rounded-full bg-black text-white h-6 w-6 hover:bg-gray-800 transition-colors"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex flex-col px-5 sm:px-6 py-6 gap-5">
                {/* Candidate Name Input */}
                <div>
                  <label
                    htmlFor="candidate-name"
                    className="block text-sm font-medium mb-2"
                    style={{ color: theme.palette.text.main }}
                  >
                    Candidate Name
                  </label>
                  <input
                    id="candidate-name"
                    type="text"
                    value={candidateName}
                    onChange={(e) => setCandidateName(e.target.value)}
                    placeholder="Enter full name"
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{
                      borderColor: theme.palette.secondary.v4,
                      color: theme.palette.text.main,
                    }}
                    autoFocus
                  />
                </div>

                {/* Upload Resume */}
                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: theme.palette.text.main }}
                  >
                    Upload resume
                  </label>
                  <input
                    ref={addCandidateFileInputRef}
                    id="file-input"
                    type="file"
                    onChange={handleAddCandidateFileChange}
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                  />
                  {!selectedFile ? (
                    <div
                      onDrop={handleAddCandidateFileDrop}
                      onDragOver={handleAddCandidateFileDragOver}
                      className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors hover:border-blue-400"
                      style={{
                        borderColor: theme.palette.secondary.v4,
                      }}
                      onClick={() => addCandidateFileInputRef.current?.click()}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <PaperClipIcon className="h-8 w-8" style={{ color: theme.palette.text.main }} />
                        <p className="text-sm" style={{ color: theme.palette.text.main }}>
                          Add an attachment
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div
                        className="flex justify-between items-center p-3 rounded-lg border"
                        style={{
                          borderColor: theme.palette.secondary.v4,
                          backgroundColor: theme.palette.primary.main,
                        }}
                      >
                        <span className="flex items-center gap-2 flex-1 min-w-0">
                          <span className="text-sm truncate" style={{ color: theme.palette.text.main }}>
                            {selectedFile.name}
                          </span>
                          <span className="text-sm whitespace-nowrap" style={{ color: theme.palette.text.secondary }}>
                            ({formatFileSize(selectedFile.size)})
                          </span>
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFile(null);
                            if (addCandidateFileInputRef.current) {
                              addCandidateFileInputRef.current.value = "";
                            }
                          }}
                          className="text-red-600 hover:text-red-800 transition-all px-2 flex-shrink-0"
                          aria-label="Remove file"
                        >
                          <span className="text-xl leading-none">&times;</span>
                        </button>
                      </div>
                    </div>
                  )}
                  <p className="text-xs mt-2" style={{ color: theme.palette.text.secondary }}>
                    Allowed: PDF, DOC, DOCX
                  </p>
                </div>
              </div>

              {addCandidateError && (
                <div className="px-5 pb-0">
                  <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded mb-2">
                    {addCandidateError}
                  </div>
                </div>
              )}

              {/* Modal Footer */}
              <div
                className="flex justify-end gap-3 px-5 py-4 border-t"
                style={{ borderColor: theme.palette.secondary.v4 }}
              >
                <Button
                  onClick={handleUploadAndContinue}
                  disabled={!selectedFile || isUploadingCandidate}
                  sx={{
                    backgroundColor: theme.palette.info.main,
                    color: theme.palette.text.light,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: theme.palette.info.dark,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: theme.palette.secondary.v2,
                      color: theme.palette.text.secondary,
                    },
                  }}
                >
                  {isUploadingCandidate ? "Uploading..." : "Upload & Continue"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidatesPage;
